import {
  formatZonedTime,
  intlLocale,
  minutesBetween,
  zoneAbbreviation as zoneAbbreviationOf,
} from '@saniplus/api-client';
import type { Locale } from '@saniplus/contracts';
import { DateTime } from 'luxon';

/**
 * The two time forms this feature is allowed to print, and nothing else.
 *
 * **Grid: `22:00–06:00`** — an en dash, four digits either side, never
 * `22–06`. The abbreviation lives in the column head, once.
 * **Prose: `22 h 00`** — with a narrow no-break space on both sides of the
 * `h`, which is the `fr-CA` typography and the only reason this is not a
 * `toFormat` call.
 *
 * Every function takes the SITE's zone. A supervisor reading a Sainte-Foy
 * night from a laptop in Vancouver must see the night the crew works, and
 * this product has four sites in four zones.
 */

/** `22:00`, in the site's zone. */
export function gridTime(iso: string, zone: string, locale: Locale): string {
  return formatZonedTime(iso, zone, locale, { form: 'grid' });
}

/** `22:00–06:00`. The en dash is the grid separator; the arrow is not used. */
export function gridRange(startIso: string, endIso: string, zone: string, locale: Locale): string {
  return `${gridTime(startIso, zone, locale)}–${gridTime(endIso, zone, locale)}`;
}

/** `HAE` / `EDT` for that instant — never for "now". */
export function zoneAbbrev(iso: string, zone: string, locale: Locale): string {
  return zoneAbbreviationOf(iso, zone, locale);
}

/** The real elapsed minutes of a shift, DST included. */
export function shiftMinutes(startIso: string, endIso: string): number {
  return minutesBetween(startIso, endIso);
}

/** `27 août` — the night label inside a column head or a band. */
export function nightLabel(businessDate: string, zone: string, locale: Locale): string {
  const dt = DateTime.fromISO(businessDate, { zone });
  if (!dt.isValid) return businessDate;
  return dt
    .setLocale(intlLocale(locale))
    .toLocaleString({ day: 'numeric', month: 'long' });
}

/** `jeudi 27 août 2026` — the long French date, for prose and dialog titles. */
export function proseDate(businessDate: string, zone: string, locale: Locale): string {
  const dt = DateTime.fromISO(businessDate, { zone });
  if (!dt.isValid) return businessDate;
  return dt
    .setLocale(intlLocale(locale))
    .toLocaleString({ weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
}

interface DateParts {
  /** `jeu` / `Thu` — the weekday, short, with the `fr-CA` period stripped. */
  readonly weekday: string;
  /** `27` — the day of the month. */
  readonly day: string;
  /** `août` / `Aug` — the month, short, with the `fr-CA` period stripped. */
  readonly month: string;
}

/**
 * `lun.` → `lun`, `sept.` → `sept`.
 *
 * The parts below are set in `label` caps, and an abbreviating period inside
 * an 0.13 em uppercase label reads as dirt on the screen rather than as
 * punctuation.
 */
function trimAbbreviation(value: string): string {
  return value.endsWith('.') ? value.slice(0, -1) : value;
}

/**
 * The business date as the three parts a column head sets separately.
 *
 * A calendar column is ~93 px. `proseDate` minus its year — which is what the
 * grid used to print — wants roughly twice that at 11 px caps, and in `en-CA`
 * the year strip left a dangling comma (`Monday, August 31,`). The weekday is
 * The weekday is a label on its own line; the day is a mono FIGURE with the
 * month beside it. Putting all three on ONE line truncated (`SAM 29 AO…`), and
 * dropping the month altogether produced a grid reading `MON 31 · TUE 1 ·
 * WED 2` — which is not a set of dates anyone can read.
 */
export function dateParts(businessDate: string, zone: string, locale: Locale): DateParts {
  const dt = DateTime.fromISO(businessDate, { zone }).setLocale(
    intlLocale(locale),
  );
  if (!dt.isValid) return { weekday: '', day: businessDate, month: '' };
  return {
    weekday: trimAbbreviation(dt.toFormat('ccc')),
    day: dt.toFormat('d'),
    month: trimAbbreviation(dt.toFormat('LLL')),
  };
}

/**
 * `29 août – 4 septembre 2026` — **which period the calendar is showing.**
 *
 * The page had a ‹ Aujourd'hui › cluster and never said what it had stepped to,
 * which is why the month was being forced into seven ~93 px column heads and
 * truncating in every one of them. `Intl.formatRange` collapses the halves the
 * two dates share on its own — `1 – 7 septembre 2026` inside one month, both
 * months when the week straddles one — which no hand-assembled string does
 * correctly in two locales.
 */
export function rangeLabel(
  view: 'day' | 'week' | 'month',
  from: string,
  to: string,
  anchor: string,
  zone: string,
  locale: Locale,
): string {
  const tag = intlLocale(locale);
  if (view === 'day') return proseDate(from, zone, locale);
  if (view === 'month') {
    const at = DateTime.fromISO(anchor, { zone });
    return at.isValid
      ? at.setLocale(tag).toLocaleString({ month: 'long', year: 'numeric' })
      : anchor;
  }
  const start = DateTime.fromISO(from, { zone });
  const end = DateTime.fromISO(to, { zone });
  if (!start.isValid || !end.isValid) return from;
  return new Intl.DateTimeFormat(tag, {
    day: 'numeric',
    month: 'long',
    year: 'numeric',
    timeZone: zone,
  }).formatRange(start.toJSDate(), end.toJSDate());
}

/**
 * `lun. 31 août` / `Mon, Aug 31` — the short date, for a band head or a
 * sentence with no room for the prose form.
 *
 * Built by `toLocaleString`, not by stripping a year off `proseDate` with a
 * regular expression: `en-CA` writes `Monday, August 31, 2026`, so that strip
 * shipped a trailing comma into every English column head and ledger band.
 */
export function shortDate(businessDate: string, zone: string, locale: Locale): string {
  const dt = DateTime.fromISO(businessDate, { zone });
  if (!dt.isValid) return businessDate;
  return dt
    .setLocale(intlLocale(locale))
    .toLocaleString({ weekday: 'short', day: 'numeric', month: 'short' });
}

/** Today, in the SITE's zone rather than the reader's. */
export function todayIn(zone: string): string {
  return DateTime.now().setZone(zone).toISODate() ?? '';
}

/** `date` shifted by whole calendar days in the site's zone — never `+ 86 400 000`. */
export function addDays(date: string, days: number, zone: string): string {
  const dt = DateTime.fromISO(date, { zone });
  if (!dt.isValid) return date;
  return dt.plus({ days }).toISODate() ?? date;
}

/** Whole calendar days between two business dates, in the site's zone. */
export function daysBetween(from: string, to: string, zone: string): number {
  const a = DateTime.fromISO(from, { zone }).startOf('day');
  const b = DateTime.fromISO(to, { zone }).startOf('day');
  if (!a.isValid || !b.isValid) return 0;
  return Math.round(b.diff(a, 'days').days);
}

/**
 * True when the two instants sit on different UTC offsets — the night a clock
 * change happens.
 *
 * It is derivable from `startsAt`/`endsAt` alone, which is exactly why the
 * *Changement d'heure* chip is allowed and the *Férié* chip is not: there is
 * no holiday source anywhere in the contract or the data model.
 */
export function crossesClockChange(startIso: string, endIso: string, zone: string): boolean {
  const start = DateTime.fromISO(startIso, { zone });
  const end = DateTime.fromISO(endIso, { zone });
  return start.isValid && end.isValid && start.offset !== end.offset;
}

/**
 * Whole hours until an instant. Negative once it has started.
 *
 * "Now" comes from luxon rather than `new Date()` so it reads the same clock as
 * every other helper here — and so a test that freezes the clock freezes the
 * urgency band with it.
 */
export function hoursUntil(iso: string, now: Date | null = null): number {
  const reference = now === null ? DateTime.now().toMillis() : now.getTime();
  return Math.floor((new Date(iso).getTime() - reference) / 3_600_000);
}

/**
 * `2026-08-27` + `22:00`, read in the SITE's zone, as the instant the API takes.
 *
 * `dayOffset` is how the end of an overnight shift is expressed: a 22:00→06:00
 * shift ends on the next calendar day, and that day is counted in the site's
 * zone with luxon rather than by adding 86 400 000 ms — the March and November
 * clock changes make those two different answers.
 */
export function instantAt(date: string, time: string, zone: string, dayOffset = 0): string {
  const [hours = '0', minutes = '0'] = time.split(':');
  const dt = DateTime.fromISO(date, { zone });
  if (!dt.isValid) return '';
  return (
    dt
      .plus({ days: dayOffset })
      .set({ hour: Number(hours), minute: Number(minutes), second: 0, millisecond: 0 })
      .toUTC()
      .toISO() ?? ''
  );
}

/** True when a wall-clock end is at or before its start — the shift runs past midnight. */
export function crossesMidnightAt(startTime: string, endTime: string): boolean {
  return endTime <= startTime;
}

/** Saturday or Sunday in the SITE's zone — what tints a column now that the
 * grid draws no vertical rules. */
export function isWeekend(businessDate: string, zone: string): boolean {
  const dt = DateTime.fromISO(businessDate, { zone });
  return dt.isValid && dt.weekday >= 6;
}

/**
 * Minutes since local midnight, right now, in the SITE's zone.
 *
 * The day view's `now` marker is the only vertical line it draws, so it is
 * read off the crew's clock like every other minute on this screen.
 */
export function minutesOfDayIn(zone: string): number | null {
  const at = DateTime.now().setZone(zone);
  return at.isValid ? at.hour * 60 + at.minute : null;
}
